import { MALDIVES_COUNTRY_CODE, MALDIVES_COUNTRY_NAME } from 'constants/charterFlight'
import Places, { PlacesByPlaceId } from 'constants/places'

/**
 * Checks if a hotel offer is located in Maldives.
 * Supports both LE Hotel offers (property.geoData.countryCode) and
 * Bedbank offers (property.address.countryCode).
 *
 * @param offer - The hotel offer to check
 * @returns true if the hotel is in Maldives
 */
export function isMaldivesHotel(offer: App.HotelOffer | App.Offer | App.OfferSummary | App.BedbankOffer | undefined): boolean {
  if (!offer) return false

  // LE Hotel offers use property.geoData.countryCode
  // Bedbank offers use property.address.countryCode
  const property = offer.property as (App.OfferProperty & { address?: App.BedbankPropertyAddress }) | undefined
  const countryCode = property?.geoData?.countryCode ?? property?.address?.countryCode
  if (!countryCode) return false

  return countryCode.toUpperCase() === MALDIVES_COUNTRY_CODE
}

/**
 * Checks if a country code or name matches Maldives.
 *
 * @param value - Country code (e.g., 'MV') or country name (e.g., 'Maldives')
 * @returns true if it matches Maldives
 */
export function isMaldivesLocation(value?: string | null): boolean {
  if (!value) return false

  const trimmed = value.trim()
  if (!trimmed) return false

  return (
    trimmed.toUpperCase() === MALDIVES_COUNTRY_CODE ||
    trimmed.toLowerCase() === MALDIVES_COUNTRY_NAME
  )
}

export function isMaldivesDestinationSearch(destinationId?: string, destinationName?: string, destinationCountry?: string | null): boolean {
  return !!destinationId?.split(',').includes(Places.Maldives.id) ||
    isMaldivesLocation(destinationName) ||
    isMaldivesLocation(destinationCountry)
}

function getDestinationIds(destinationId: string | undefined): Array<string> {
  return destinationId?.split(',').filter(Boolean) ?? []
}

function canClassifyDestinationIdWithoutPlaceLookup(destinationId: string): boolean {
  return !!PlacesByPlaceId[destinationId]?.countryCode
}

/**
 * Wait for place lookup only while a destination could still resolve to a Maldives
 * island/atoll. Known catalog places (Bali, Sydney, …) and already-classified
 * Maldives country searches must not block CRO activation.
 *
 * Stay pending when lookup is in flight, missing, or settled as null/error so
 * unclassified atolls cannot fail open into the CRO experiment.
 * Comma-separated ids skip `usePlaceById`; keep those searches pending unless
 * every id already has a catalog country code.
 */
export function isMaldivesDestinationCountryPending(
  destinationId: string | undefined,
  destinationName: string | undefined,
  destinationPlace: Pick<App.Place, 'countryCode'> | undefined,
  shouldSkipPlaceLookup: boolean,
): boolean {
  if (shouldSkipPlaceLookup) {
    const destinationIds = getDestinationIds(destinationId)
    if (destinationIds.length <= 1) return false
    return destinationIds.some(id => !canClassifyDestinationIdWithoutPlaceLookup(id))
  }
  if (isMaldivesDestinationSearch(destinationId, destinationName, destinationPlace?.countryCode)) {
    return false
  }

  const catalogCountryCode = destinationId ? PlacesByPlaceId[destinationId]?.countryCode : undefined
  if (destinationPlace?.countryCode || catalogCountryCode) return false

  return true
}
